import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';

// ── Picklist metadata ──────────────────────────────────────────────────────────
// Each entry describes one picklist table.
// labelField: the column that holds the display label
// hasMultiplier: if true, show a decimal multiplier input alongside the label
const PICKLIST_META = [
  { key: 'artist_types',        label: 'Artist Types',              labelField: 'value' },
  { key: 'artist_styles',       label: 'Artist Styles',             labelField: 'value' },
  { key: 'venue_statuses',      label: 'Venue Statuses',            labelField: 'value' },
  { key: 'venue_types',         label: 'Venue Types',               labelField: 'value' },
  { key: 'venue_age_limits',    label: 'Venue Age Limits',          labelField: 'value' },
  { key: 'attendance_estimates',label: 'Attendance Estimates',      labelField: 'value', hasMultiplier: true },
];

const S = {
  page: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '40px 24px',
    color: '#e0e0e0',
    fontFamily: 'sans-serif',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#bb86fc',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '0 0 16px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  title: {
    fontSize: '26px',
    fontWeight: 'bold',
    color: '#bb86fc',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '13px',
    color: '#777',
    marginBottom: '32px',
  },
  tabBar: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '28px',
    borderBottom: '1px solid #2a2a2a',
    paddingBottom: '12px',
  },
  tab: {
    padding: '7px 14px',
    borderRadius: '6px',
    border: '1px solid #333',
    backgroundColor: '#121212',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.15s',
  },
  tabActive: {
    backgroundColor: '#bb86fc',
    borderColor: '#bb86fc',
    color: '#000',
    fontWeight: 'bold',
  },
  card: {
    backgroundColor: '#1e1e2e',
    border: '1px solid #2a2a2a',
    borderRadius: '10px',
    padding: '24px',
  },
  tableHeader: {
    display: 'grid',
    gap: '8px',
    padding: '0 8px 8px',
    borderBottom: '1px solid #2a2a2a',
    marginBottom: '4px',
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  row: {
    display: 'grid',
    gap: '8px',
    alignItems: 'center',
    padding: '8px',
    borderRadius: '6px',
    transition: 'background-color 0.1s',
  },
  input: {
    backgroundColor: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '6px',
    color: '#e0e0e0',
    padding: '7px 10px',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputSmall: {
    backgroundColor: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '6px',
    color: '#e0e0e0',
    padding: '7px 10px',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  idBadge: {
    fontSize: '11px',
    color: '#555',
    fontFamily: 'monospace',
    paddingTop: '2px',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    borderRadius: '4px',
    fontSize: '15px',
    transition: 'background 0.15s',
  },
  addRow: {
    display: 'grid',
    gap: '8px',
    alignItems: 'center',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #2a2a2a',
  },
  addBtn: {
    backgroundColor: '#bb86fc',
    color: '#000',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 'bold',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  errorMsg: {
    color: '#e57373',
    fontSize: '13px',
    marginTop: '12px',
  },
  successMsg: {
    color: '#81c784',
    fontSize: '13px',
    marginTop: '12px',
  },
  multiplierHint: {
    fontSize: '11px',
    color: '#555',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  saveBtn: {
    backgroundColor: '#2a7',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    padding: '5px 10px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
};

async function getAuthHeader() {
  const { tokens } = await fetchAuthSession();
  return { Authorization: tokens.idToken.toString() };
}

export default function AdminPicklists() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(PICKLIST_META[0].key);
  const [picklists, setPicklists] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPicklists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeader();
      const res = await axios.get(`${config.API_URL}/admin/picklists`, { headers });
      setPicklists(res.data);
    } catch (e) {
      setError('Failed to load picklists: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPicklists(); }, [loadPicklists]);

  const activeMeta = PICKLIST_META.find(m => m.key === activeTab);
  const activeRows = picklists[activeTab] || [];

  return (
    <div style={S.page}>
      <button style={S.backBtn} onClick={() => navigate('/admin')}>
        ← Back to Admin Portal
      </button>
      <h1 style={S.title}>Add/Update Picklist Values</h1>
      <p style={S.subtitle}>
        Manage the dropdown options used across SHARC forms.
      </p>

      {/* Tab bar */}
      <div style={S.tabBar}>
        {PICKLIST_META.map(m => (
          <button
            key={m.key}
            style={activeTab === m.key ? { ...S.tab, ...S.tabActive } : S.tab}
            onClick={() => setActiveTab(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ color: '#777' }}>Loading…</div>}
      {error && <div style={S.errorMsg}>{error}</div>}

      {!loading && !error && activeMeta && (
        <PicklistEditor
          meta={activeMeta}
          rows={activeRows}
          onReload={loadPicklists}
        />
      )}
    </div>
  );
}

// ── Single picklist editor ─────────────────────────────────────────────────────
function PicklistEditor({ meta, rows, onReload }) {
  const [editStates, setEditStates] = useState({});
  const [newLabel, setNewLabel] = useState('');
  const [newMultiplier, setNewMultiplier] = useState('');
  const [saving, setSaving] = useState(null); // id of row being saved
  const [adding, setAdding] = useState(false);
  const [flash, setFlash] = useState(null); // { type: 'success'|'error', msg }

  const hasMultiplier = !!meta.hasMultiplier;

  // Sort ascending — attendance_estimates by multiplier value (numeric order),
  // all other picklists alphabetically by value
  const sortedRows = hasMultiplier
    ? [...rows].sort((a, b) => (a.multiplier ?? 999) - (b.multiplier ?? 999))
    : [...rows].sort((a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }));

  // Grid column template
  const gridCols = hasMultiplier
    ? '50px 1fr 130px 80px'   // id | label | multiplier | actions
    : '50px 1fr 80px';        // id | label | actions

  function showFlash(type, msg) {
    setFlash({ type, msg });
    setTimeout(() => setFlash(null), 3000);
  }

  function startEdit(row) {
    setEditStates(prev => ({
      ...prev,
      [row.id]: {
        value: row.value,
        multiplier: row.multiplier != null ? String(row.multiplier) : '',
      },
    }));
  }

  function cancelEdit(id) {
    setEditStates(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  async function saveEdit(row) {
    const state = editStates[row.id];
    if (!state?.value?.trim()) return;
    setSaving(row.id);
    try {
      const headers = await getAuthHeader();
      const body = { value: state.value.trim() };
      if (hasMultiplier) body.multiplier = state.multiplier !== '' ? parseFloat(state.multiplier) : null;
      await axios.put(`${config.API_URL}/admin/picklists/${meta.key}/${row.id}`, body, { headers });
      cancelEdit(row.id);
      showFlash('success', 'Saved!');
      onReload();
    } catch (e) {
      showFlash('error', e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(null);
    }
  }

  async function deleteRow(row) {
    if (!window.confirm(`Delete "${row.value}"? This cannot be undone.`)) return;
    setSaving(row.id);
    try {
      const headers = await getAuthHeader();
      await axios.delete(`${config.API_URL}/admin/picklists/${meta.key}/${row.id}`, { headers });
      showFlash('success', 'Deleted.');
      onReload();
    } catch (e) {
      showFlash('error', e.response?.data?.error || 'Delete failed');
    } finally {
      setSaving(null);
    }
  }

  async function addRow() {
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      const headers = await getAuthHeader();
      const body = { value: newLabel.trim() };
      if (hasMultiplier) body.multiplier = newMultiplier !== '' ? parseFloat(newMultiplier) : null;
      await axios.post(`${config.API_URL}/admin/picklists/${meta.key}`, body, { headers });
      setNewLabel('');
      setNewMultiplier('');
      showFlash('success', 'Added!');
      onReload();
    } catch (e) {
      showFlash('error', e.response?.data?.error || 'Add failed');
    } finally {
      setAdding(false);
    }
  }

  return (
    <div style={S.card}>
      {hasMultiplier && (
        <div style={S.multiplierHint}>
          💡 <strong>Multiplier</strong> is a decimal (0–1) applied to venue or stage capacity
          to estimate attendance. Example: <em>~25% Capacity</em> → multiplier <em>0.25</em>.
        </div>
      )}

      {/* Column headers */}
      <div style={{ ...S.tableHeader, gridTemplateColumns: gridCols }}>
        <span>ID</span>
        <span>Label</span>
        {hasMultiplier && <span>Multiplier</span>}
        <span>Actions</span>
      </div>

      {/* Rows */}
      {sortedRows.map(row => {
        const editing = !!editStates[row.id];
        const es = editStates[row.id] || {};
        const isSaving = saving === row.id;

        return (
          <div
            key={row.id}
            style={{
              ...S.row,
              gridTemplateColumns: gridCols,
              backgroundColor: editing ? '#22203a' : 'transparent',
            }}
          >
            <span style={S.idBadge}>{row.id}</span>

            {editing ? (
              <input
                style={S.input}
                value={es.value}
                onChange={e => setEditStates(prev => ({
                  ...prev,
                  [row.id]: { ...prev[row.id], value: e.target.value }
                }))}
              />
            ) : (
              <span style={{ fontSize: '14px' }}>{row.value}</span>
            )}

            {hasMultiplier && (editing ? (
              <input
                style={S.inputSmall}
                type="number"
                min="0"
                max="1"
                step="0.01"
                placeholder="e.g. 0.25"
                value={es.multiplier}
                onChange={e => setEditStates(prev => ({
                  ...prev,
                  [row.id]: { ...prev[row.id], multiplier: e.target.value }
                }))}
              />
            ) : (
              <span style={{ fontSize: '13px', color: row.multiplier != null ? '#bb86fc' : '#444', fontFamily: 'monospace' }}>
                {row.multiplier != null ? row.multiplier : '—'}
              </span>
            ))}

            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {editing ? (
                <>
                  <button
                    style={{ ...S.saveBtn, opacity: isSaving ? 0.6 : 1 }}
                    onClick={() => saveEdit(row)}
                    disabled={isSaving}
                  >
                    {isSaving ? '…' : '✓'}
                  </button>
                  <button
                    style={{ ...S.iconBtn, color: '#777' }}
                    onClick={() => cancelEdit(row.id)}
                    title="Cancel"
                  >
                    ✕
                  </button>
                </>
              ) : (
                <>
                  <button
                    style={{ ...S.iconBtn, color: '#aaa' }}
                    onClick={() => startEdit(row)}
                    title="Edit"
                    disabled={isSaving}
                  >
                    ✏️
                  </button>
                  <button
                    style={{ ...S.iconBtn, color: '#e57373' }}
                    onClick={() => deleteRow(row)}
                    title="Delete"
                    disabled={isSaving}
                  >
                    🗑️
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* Add new row */}
      <div style={{ ...S.addRow, gridTemplateColumns: gridCols }}>
        <span style={{ ...S.idBadge, color: '#444' }}>new</span>
        <input
          style={S.input}
          placeholder="New label…"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addRow()}
        />
        {hasMultiplier && (
          <input
            style={S.inputSmall}
            type="number"
            min="0"
            max="1"
            step="0.01"
            placeholder="0.00–1.00"
            value={newMultiplier}
            onChange={e => setNewMultiplier(e.target.value)}
          />
        )}
        <button
          style={{ ...S.addBtn, opacity: adding ? 0.6 : 1 }}
          onClick={addRow}
          disabled={adding || !newLabel.trim()}
        >
          {adding ? 'Adding…' : '+ Add'}
        </button>
      </div>

      {/* Flash message */}
      {flash && (
        <div style={flash.type === 'success' ? S.successMsg : S.errorMsg}>
          {flash.msg}
        </div>
      )}
    </div>
  );
}
